
import React, { useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Filter } from "lucide-react";
import { useHierarchy } from '@/contexts/HierarchyContext';
import { Badge } from '@/components/ui/badge';
import { HierarchyRole } from '@/types/hierarchy';

interface HierarchyFilterProps {
  onFilterChange: (filters: {
    organization?: string;
    department?: string;
    location?: string;
    accessLevel?: string;
    deviceType?: string;
  }) => void;
}

const HierarchyFilter: React.FC<HierarchyFilterProps> = ({ onFilterChange }) => {
  const { hierarchyData, userRole, setUserRole, canAccess } = useHierarchy();
  const [filters, setFilters] = useState({
    organization: '',
    department: '',
    location: '',
    accessLevel: '',
    deviceType: '',
  });
  const [activeFiltersCount, setActiveFiltersCount] = useState(0);

  const handleFilterChange = (key: string, value: string) => {
    const newFilters = { ...filters, [key]: value };
    
    // Reset dependent filters when parent filter changes
    if (key === 'organization') {
      newFilters.department = '';
      newFilters.location = '';
    } else if (key === 'department') {
      newFilters.location = '';
    }
    
    setFilters(newFilters);
    onFilterChange(newFilters);
    
    // Count active filters
    const count = Object.values(newFilters).filter(v => v !== '').length;
    setActiveFiltersCount(count);
  };

  const resetFilters = () => {
    const emptyFilters = {
      organization: '',
      department: '',
      location: '',
      accessLevel: '',
      deviceType: '',
    };
    setFilters(emptyFilters);
    onFilterChange(emptyFilters);
    setActiveFiltersCount(0);
  };

  // Roles for simulation
  const roles: HierarchyRole[] = [
    'system-administrator',
    'security-officer',
    'network-administrator',
    'department-manager',
    'technician',
    'standard-user'
  ];

  const roleDisplayNames: Record<HierarchyRole, string> = {
    'system-administrator': 'System Administrator',
    'security-officer': 'Security Officer',
    'network-administrator': 'Network Administrator',
    'department-manager': 'Department Manager',
    'technician': 'Technician',
    'standard-user': 'Standard User'
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="h-9 relative">
          <Filter className="h-4 w-4 mr-2" />
          Hierarchy Filter
          {activeFiltersCount > 0 && (
            <Badge variant="secondary" className="ml-2 h-5 min-w-5 px-1 rounded-full">
              {activeFiltersCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-72">
        <DropdownMenuLabel>Filter By Hierarchy</DropdownMenuLabel>
        <DropdownMenuSeparator />
        
        {/* Organization Filter */}
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <span>Organization</span>
            {filters.organization && (
              <Badge variant="outline" className="ml-2">
                {hierarchyData.organizations.find(o => o.id === filters.organization)?.name || 'Any'}
              </Badge>
            )}
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuItem onClick={() => handleFilterChange('organization', '')}>
              Any
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {hierarchyData.organizations
              .filter(org => canAccess(org.accessLevel))
              .map(org => (
                <DropdownMenuItem 
                  key={org.id} 
                  onClick={() => handleFilterChange('organization', org.id)}
                >
                  {org.name}
                </DropdownMenuItem>
              ))}
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        
        {/* Department Filter */}
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <span>Department</span>
            {filters.department && (
              <Badge variant="outline" className="ml-2">
                {hierarchyData.departments.find(d => d.id === filters.department)?.name || 'Any'}
              </Badge>
            )}
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuItem onClick={() => handleFilterChange('department', '')}>
              Any
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {hierarchyData.departments
              .filter(dept => 
                canAccess(dept.accessLevel) && 
                (!filters.organization || dept.organizationId === filters.organization)
              )
              .map(dept => (
                <DropdownMenuItem 
                  key={dept.id} 
                  onClick={() => handleFilterChange('department', dept.id)}
                >
                  {dept.name}
                </DropdownMenuItem>
              ))}
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        
        {/* Location Filter */}
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <span>Location</span>
            {filters.location && (
              <Badge variant="outline" className="ml-2">
                {hierarchyData.locations.find(l => l.id === filters.location)?.name || 'Any'}
              </Badge>
            )}
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuItem onClick={() => handleFilterChange('location', '')}>
              Any
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {hierarchyData.locations
              .filter(loc => 
                canAccess(loc.accessLevel) && 
                (!filters.department || loc.departmentId === filters.department)
              )
              .map(loc => (
                <DropdownMenuItem 
                  key={loc.id} 
                  onClick={() => handleFilterChange('location', loc.id)}
                >
                  {loc.name}
                </DropdownMenuItem>
              ))}
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        
        {/* Device Type Filter */}
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <span>Device Type</span>
            {filters.deviceType && (
              <Badge variant="outline" className="ml-2">{filters.deviceType}</Badge>
            )}
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuItem onClick={() => handleFilterChange('deviceType', '')}>
              Any
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {['Gateway', 'Sensor', 'Actuator', 'Camera'].map(type => (
              <DropdownMenuItem 
                key={type} 
                onClick={() => handleFilterChange('deviceType', type)}
              >
                {type}
              </DropdownMenuItem>
            ))}
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        
        {/* Access Level Filter */}
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <span>Security Level</span>
            {filters.accessLevel && (
              <Badge variant="outline" className="ml-2 capitalize">{filters.accessLevel}</Badge>
            )}
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuItem onClick={() => handleFilterChange('accessLevel', '')}>
              Any
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {['top-secret', 'secret', 'confidential', 'restricted', 'unclassified']
              .filter(level => canAccess(level as any))
              .map(level => (
                <DropdownMenuItem 
                  key={level} 
                  onClick={() => handleFilterChange('accessLevel', level)}
                >
                  <span className="capitalize">{level}</span>
                </DropdownMenuItem>
              ))}
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        
        <DropdownMenuSeparator />
        
        {/* Simulate different user roles */}
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <span>Simulate User Role</span>
            <Badge variant="outline" className="ml-2">
              {roleDisplayNames[userRole]}
            </Badge>
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuRadioGroup value={userRole} onValueChange={(value) => setUserRole(value as HierarchyRole)}>
              {roles.map(role => (
                <DropdownMenuRadioItem key={role} value={role}>
                  {roleDisplayNames[role]}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={resetFilters}>Reset All Filters</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default HierarchyFilter;
